/**
 * A v4 uuid from the platform's CSPRNG.
 *
 * `crypto` is read off `globalThis` and every use is guarded: it is absent
 * entirely in the Electron main process, and `crypto.randomUUID` alone is
 * absent at a bare-IP http origin (a non-secure context). An absent CSPRNG
 * THROWS with the situation named — `Math.random` is not a substitute for an
 * identifier the backend treats as a device's name.
 */
function csprng(): Crypto | null {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  return typeof c?.getRandomValues === 'function' ? c : null;
}

export function uuidV4(): string {
  const c = csprng();
  if (c === null) {
    throw new Error(
      'uuidV4: no Web Crypto on globalThis. The minted device identity is the ' +
        'web-only rung (sdk ADR-009) — a Node or ' +
        'Electron-main client must resolve its identity from the daemon or an ' +
        'explicit pin, never mint one.',
    );
  }
  if (typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  // Non-secure context: randomUUID is absent but getRandomValues is not.
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // variant 10
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0'));
  return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
}
